import { CustomCellEditorProps } from 'ag-grid-react'
import { styled } from '@mui/material'
import { KeyboardEvent, useLayoutEffect, useRef, useState } from 'react'
import { ProgressReportTableContent } from '../table/ProgressReportTable'
import { ReportStatusIcon } from '../status-icon'
import { VersionReportStatus } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { colorPalette } from '../../../../style/colorPalette'

const OPTIONS: VersionReportStatus[] = ['SUNNY', 'CLOUDY', 'RAINY']

export const ReportStatusCellEditor = ({
  value,
  api,
  onValueChange,
}: CustomCellEditorProps<ProgressReportTableContent, VersionReportStatus>) => {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    const index = value ? OPTIONS.indexOf(value) : -1
    return index >= 0 ? index : 0
  })
  const panelRef = useRef<HTMLDivElement>(null)

  // 上下キーで選択肢を移動できるよう、編集開始時にパネルへフォーカスを移す。
  useLayoutEffect(() => {
    panelRef.current?.focus()
  }, [])

  const confirm = (status: VersionReportStatus, moveDown: boolean) => {
    onValueChange(status)
    const focused = api.getFocusedCell()
    api.stopEditing()
    // Enter での確定時のみ、grid 標準の編集後 Enter と同様に1つ下のセルへ移動する。
    if (moveDown && focused) {
      const next = focused.rowIndex + 1
      if (next <= api.getDisplayedRowCount() - 1) {
        api.setFocusedCell(next, focused.column)
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => (prev + 1) % OPTIONS.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => (prev - 1 + OPTIONS.length) % OPTIONS.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      confirm(OPTIONS[selectedIndex], true)
    }
  }

  return (
    <Panel ref={panelRef} tabIndex={0} onKeyDown={handleKeyDown}>
      {OPTIONS.map((status, index) => (
        <IconButton
          key={status}
          type="button"
          selected={index === selectedIndex}
          onClick={() => confirm(status, false)}
        >
          <ReportStatusIcon status={status} size={24} />
        </IconButton>
      ))}
    </Panel>
  )
}

const Panel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '2px',
  padding: '4px',
  minWidth: '90px',
  boxSizing: 'border-box',
  background: colorPalette.monotone[0],
  borderRadius: '4px',
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  '&:focus': {
    outline: 'none',
  },
})

const IconButton = styled('button')<{ selected: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '4px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  background: selected ? colorPalette.skyBlue[1] : 'transparent',
  '&:hover': {
    background: colorPalette.skyBlue[0],
  },
}))
